import { memo, useCallback, useState } from "react";

import { ImageModal } from "@/components/features/faq-detail/ui/components/markdown-content/components/image-modal";

import { Checkbox } from "@/components/ui/input/checkbox";

type Props = {
	checkedItems: boolean[];
	onCheckedChange: (index: number, checked: boolean) => void;
};

const CHECKLIST_ITEMS = [
	"設定した料金・プラン内容が正しく表示されていますか",
	"登録した基本情報・画像・文言に誤りはありませんか",
	"お客様に送信される予約完了メールの内容に誤りはありませんか",
	"施設側（管理者）に予約通知メールが届きますか",
	"決済情報・料金プランの登録ができていますか",
] as const;

export const STEP2_ITEM_COUNT = CHECKLIST_ITEMS.length;

export const Step2 = memo(({ checkedItems, onCheckedChange }: Props) => {
	const [modalImage, setModalImage] = useState<{ src: string; alt: string } | null>(null);

	const handleImageClick = useCallback((src: string, alt: string) => {
		setModalImage({ src, alt });
	}, []);

	const handleCloseModal = useCallback(() => {
		setModalImage(null);
	}, []);

	return (
		<section className="space-y-4">
			<ImageModal
				src={modalImage?.src ?? ""}
				alt={modalImage?.alt ?? ""}
				open={modalImage !== null}
				onOpenChange={(open) => {
					if (!open) handleCloseModal();
				}}
			/>
			<h3 className="border-b border-zinc-200 pb-2 text-base font-bold">STEP2. 販売開始前の確認事項</h3>
			<p className="text-sm leading-relaxed text-zinc-700">
				販売開始前には、
				<span className="font-bold">表示内容・通知・決済まわりの設定</span>
				を必ず確認しましょう。
			</p>
			<p className="text-sm font-bold text-zinc-800">チェックポイント</p>
			<div className="space-y-3">
				{CHECKLIST_ITEMS.map((item, index) => (
					<div
						key={item}
						className={`rounded-lg border border-zinc-200 p-4 hover:bg-zinc-50 ${
							index === 0 ? "flex flex-col gap-3" : ""
						}`}
					>
						<label className="flex cursor-pointer items-start gap-x-3">
							<Checkbox
								checked={checkedItems[index]}
								onCheckedChange={(checked) => onCheckedChange(index, checked === true)}
								className="mt-0.5"
							/>
							<span className="text-sm leading-relaxed">{item}</span>
						</label>
						{index === 0 && (
							<div className="flex flex-nowrap gap-3 overflow-x-auto pl-7">
								<img
									src="/assets/start-guide-todo-list/check-price-reservation_1.png"
									alt="予約内容の確認画面"
									className="h-auto w-[min(200px,30vw)] shrink-0 cursor-zoom-in rounded-md border border-zinc-200 object-contain transition-shadow hover:shadow-md"
									draggable={false}
									onClick={() =>
										handleImageClick(
											"/assets/start-guide-todo-list/check-price-reservation_1.png",
											"予約内容の確認画面",
										)
									}
								/>
								<img
									src="/assets/start-guide-todo-list/check-price-reservation_2.png"
									alt="プラン選択画面"
									className="h-auto w-[min(200px,30vw)] shrink-0 cursor-zoom-in rounded-md border border-zinc-200 object-contain transition-shadow hover:shadow-md"
									draggable={false}
									onClick={() =>
										handleImageClick(
											"/assets/start-guide-todo-list/check-price-reservation_2.png",
											"プラン選択画面",
										)
									}
								/>
								<img
									src="/assets/start-guide-todo-list/check-price-reservation_3.png"
									alt="日付・料金カレンダー画面"
									className="h-auto w-[min(200px,30vw)] shrink-0 cursor-zoom-in rounded-md border border-zinc-200 object-contain transition-shadow hover:shadow-md"
									draggable={false}
									onClick={() =>
										handleImageClick(
											"/assets/start-guide-todo-list/check-price-reservation_3.png",
											"日付・料金カレンダー画面",
										)
									}
								/>
							</div>
						)}
						{index === CHECKLIST_ITEMS.length - 1 && (
							<div className="mt-4 flex flex-col gap-3">
								<p className="text-amber-800 text-sm leading-relaxed">
									重要：入金先情報が未設定の場合、
									<span className="font-bold">事前決済の予約を受け付けることができません。</span>
									販売開始前に、必ず
									<span className="font-bold">「設定 ＞ 入金先情報」</span>
									からご設定ください。
								</p>
								<img
									src="/assets/start-guide-todo-list/settlement.png"
									alt="入金先情報の設定画面"
									className="h-auto w-[min(200px,30vw)] max-w-sm shrink-0 cursor-zoom-in rounded-md border border-zinc-200 object-contain transition-shadow hover:shadow-md"
									draggable={false}
									onClick={() =>
										handleImageClick(
											"/assets/start-guide-todo-list/settlement.png",
											"入金先情報の設定画面",
										)
									}
								/>
							</div>
						)}
					</div>
				))}
			</div>
		</section>
	);
});

Step2.displayName = "Step2";
